import React, { useEffect, useMemo, useState } from 'react';
import Dropzone from 'react-dropzone';
import { FormHelperText, IconButton, Stack, Typography, styled } from '@mui/material';
import { isEmpty } from 'lodash';

import Icon from '../Icon';
import styles from './upload.module.scss';
import { UploadComponentProps } from './types';
import UploadedVideoInfo from './UploadedVideoInfo';

const UploadContainer = styled('div', {
  shouldForwardProp: props => props !== 'isFocused' && props !== 'isDragReject' && props !== 'isDragActive'
})<{ isFocused: boolean; isDragReject: boolean; isDragActive: boolean }>(
  ({ theme, isFocused, isDragReject, isDragActive }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    border: `1px dashed ${
      (isFocused || isDragActive) && !isDragReject
        ? theme.palette.primary.dark
        : isDragReject && isDragActive
        ? theme.palette.error.main
        : theme.palette.primary.main
    } `,
    background:
      (isFocused || isDragActive) && !isDragReject
        ? 'rgba(131, 60, 226, 0.1)'
        : isDragReject && isDragActive
        ? theme.palette.error.light
        : 'rgba(131, 60, 226, 0.05)',
    borderRadius: '12px',
    textAlign: 'center',
    height: '100%',
    width: '100%'
  })
);

const VideoComponent = ({ uploadedFile }: { uploadedFile: File[] }) =>
  useMemo(
    () => <video className={styles.video} autoPlay muted controls src={URL.createObjectURL(uploadedFile[0])} />,
    [uploadedFile]
  );

const ImageComponent = ({ uploadedFile }: { uploadedFile: File[] }) =>
  useMemo(
    () => <img style={{ maxWidth: '100%', width: 100 }} src={URL.createObjectURL(uploadedFile[0])} />,
    [uploadedFile]
  );

const acceptTypes = {
  video: { 'video/mp4': ['.mp4', '.MP4'] },
  image: {
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
    'image/svg+xml': ['.svg']
  },
  icon: { 'image/svg+xml': ['.svg'] }
};

const UploadComponent = ({
  error,
  helperText,
  handleChange,
  type = 'video',
  height = 250,
  layout = 'vertical',
  getInputProps: customGetInputProps,
  acceptType,
  file,
  ...props
}: UploadComponentProps) => {
  const [uploadedFile, setFiles] = useState<File[]>([]);
  const handleDrop = (files: File[]) => {
    setFiles(files);
    handleChange(files[0]);
  };

  useEffect(() => {
    if (file) {
      setFiles([file]);
    }
  }, [file]);

  const getComponentType = () => {
    switch (type) {
      case 'video':
        return <UploadedVideoInfo file={uploadedFile[0]} />;
      case 'image':
        return <ImageComponent uploadedFile={uploadedFile} />;
      case 'icon':
        return <ImageComponent uploadedFile={uploadedFile} />;
      default:
        return <VideoComponent uploadedFile={uploadedFile} />;
    }
  };

  return (
    <>
      <Dropzone {...props} onDrop={handleDrop} accept={acceptType ? acceptType : acceptTypes[type]}>
        {({ getRootProps, getInputProps, isFocused, isDragReject, isDragActive }) => {
          return (
            <Stack height={height} width="100%" alignItems="center" justifyContent="center" position="relative">
              {isEmpty(uploadedFile) ? (
                <UploadContainer
                  {...getRootProps({ className: 'dropzone' })}
                  isFocused={isFocused}
                  isDragReject={isDragReject}
                  isDragActive={isDragActive}
                >
                  <input {...(customGetInputProps ? customGetInputProps(getInputProps()) : getInputProps())} />
                  <Stack
                    direction={layout === 'horizontal' ? 'row' : 'column'}
                    spacing={layout === 'horizontal' ? 1 : 0}
                    alignItems="center"
                  >
                    <Icon name="galary" />
                    <Typography component="p" variant="body1">
                      Drag & Drop Asset here, or{' '}
                      <Typography component="span" variant="body1" sx={{ color: 'primary.main' }}>
                        Browse
                      </Typography>
                    </Typography>
                  </Stack>
                </UploadContainer>
              ) : (
                <>
                  <IconButton
                    onClick={() => {
                      setFiles([]);
                      handleChange(null);
                    }}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      color: 'error.main',
                      zIndex: 1,
                      borderRadius: '4px',
                      padding: '0'
                    }}
                  >
                    <Icon name="close" size={'24'} />
                  </IconButton>
                  {getComponentType()}
                </>
              )}
            </Stack>
          );
        }}
      </Dropzone>
      <FormHelperText error={error}>{helperText}</FormHelperText>
    </>
  );
};

export default UploadComponent;
